import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { clearStoredSession, markSessionActive } from '@/utils/authSession'


export interface UserInfo {
  userID?: string
  salesId?: number
  salesTaxId?: string
  salesName?: string
  companyPhoneNumberExt?: string
  branchId?: number
  branchCode?: string
  branchIdTaxId?: string
  branchName?: string
  distributorId?: number
  distributorName?: string
  role?: string
  systemRole?: string | null
  roleCode?: string
  scopeType?: string
  permissions?: string[]
  displayName?: string  
}

export const useUserStore = defineStore('user', () => {
  // 🚀 初始化時，直接從 localStorage 載入
  const token = ref(localStorage.getItem('token') || '')

  const userInfoStr = localStorage.getItem('userInfo')
  const loadUserInfo = (): UserInfo | null => {
    try {
      return userInfoStr ? (JSON.parse(userInfoStr) as UserInfo) : null
    } catch {
      localStorage.removeItem('userInfo')
      return null
    }
  }
  const userInfo = ref<UserInfo | null>(loadUserInfo())

  // 🌟 Getters (響應式計算屬性)
  const isLoggedIn = computed(() => !!token.value)
  const salesName = computed(() => userInfo.value?.salesName || '未知業務員')
  const branchName = computed(() => userInfo.value?.branchName || '未知據點')
  const distributorName = computed(() => userInfo.value?.distributorName || '未知通路')
  const currentBranchId = computed(() => userInfo.value?.branchId || null)
  const role = computed(() => userInfo.value?.role || null)
  const roleCode = computed(() => userInfo.value?.roleCode || null)
  const hasPermission = (permission?: string): boolean =>
    !permission || Boolean(userInfo.value?.permissions?.includes(permission))
  const hasRole = (allowedRoles?: string[]): boolean => {
    if (!allowedRoles || allowedRoles.length === 0) return true

    const userSysRole = userInfo.value?.systemRole
    if (!userSysRole) return false

    return allowedRoles.includes(userSysRole)
  }


  // 🌟 Actions (處理登入與登出)
  const setLoginInfo = (jwtToken: string, userObj: UserInfo) => {
    token.value = jwtToken
    userInfo.value = userObj

    localStorage.setItem('token', jwtToken)
    localStorage.setItem('userInfo', JSON.stringify(userObj))
    markSessionActive()
  }

  const syncFromStorage = () => {
    token.value = localStorage.getItem('token') || ''
    const value = localStorage.getItem('userInfo')
    userInfo.value = value ? (JSON.parse(value) as UserInfo) : null
  }

  const logout = () => {
    token.value = ''
    userInfo.value = null
    clearStoredSession()
  }

  return {
    token, userInfo, isLoggedIn, salesName, branchName, distributorName,
    currentBranchId, role, roleCode, hasRole, hasPermission, setLoginInfo, syncFromStorage, logout,
  }
})
